import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../lib/api'

/**
 * Detailed delivery reporting, broken down by sending domain.
 *
 * The Overview gives one tenant-wide number; this answers the question that actually decides
 * reputation work — WHICH domain is bouncing, which is getting opened, which never gets past
 * the receiver. One row per domain, plus a headline total, over a selectable window.
 */
const RANGES = [
  { days: 7, label: '7 days' },
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' },
]

function pct(n: number | null): string {
  return n == null ? '—' : `${n.toFixed(1)}%`
}

const SORTS = [
  { key: 'messages', label: 'Most messages' },
  { key: 'delivered', label: 'Most delivered' },
  { key: 'bounce_rate', label: 'Highest bounce %' },
  { key: 'delivery_rate', label: 'Lowest delivery %' },
  { key: 'opened', label: 'Most opened' },
  { key: 'domain', label: 'Domain A–Z' },
]

export function Reporting() {
  const [days, setDays] = useState(30)
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState('messages')
  const [problemsOnly, setProblemsOnly] = useState(false)
  const { data, isLoading } = useQuery({
    queryKey: ['report-by-domain', days],
    queryFn: () => api.reports.byDomain(days),
  })

  const allRows = data?.domains ?? []
  const t = data?.totals

  // Client-side filter + sort — the report set is small (one row per domain).
  const rows = (() => {
    let r = allRows.filter(x => !search.trim() || x.domain.toLowerCase().includes(search.trim().toLowerCase()))
    if (problemsOnly) r = r.filter(x => (x.bounce_rate != null && x.bounce_rate > 5) || (x.delivery_rate != null && x.delivery_rate < 90))
    return [...r].sort((a, b) => {
      if (sort === 'domain') return a.domain.localeCompare(b.domain)
      if (sort === 'delivery_rate') return (a.delivery_rate ?? 101) - (b.delivery_rate ?? 101) // lowest first
      return ((b as any)[sort] ?? 0) - ((a as any)[sort] ?? 0)
    })
  })()

  function exportCsv() {
    const cols = ['Domain', 'Messages', 'Recipients', 'Delivered', 'Bounced', 'Deferred',
      'Complained', 'Suppressed', 'Opened', 'Clicked', 'Delivery %', 'Bounce %']
    const esc = (v: any) => { const s = v == null ? '' : String(v); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s }
    const lines = [cols.join(',')]
    for (const r of rows) {
      lines.push([r.domain, r.messages, r.recipients, r.delivered, r.bounced, r.deferred,
        r.complained, r.suppressed, r.opened, r.clicked, r.delivery_rate ?? '', r.bounce_rate ?? ''].map(esc).join(','))
    }
    const blob = new Blob(['﻿' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8' })
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = `itsbingmybusiness-report-${new Date().toISOString().slice(0, 10)}.csv`
    a.click(); URL.revokeObjectURL(a.href)
  }

  return (
    <div>
      <h1>Reporting</h1>
      <p className="lede">
        Delivery outcomes broken down by sending domain — so you can see exactly which domain is
        healthy and which needs attention, not just one blended number.
      </p>

      <div className="toolbar">
        <div className="segmented" role="tablist" aria-label="Report window">
          {RANGES.map(r => (
            <button key={r.days} role="tab" aria-selected={days === r.days}
                    className={days === r.days ? 'active' : ''} onClick={() => setDays(r.days)}>
              {r.label}
            </button>
          ))}
        </div>
        <input placeholder="Filter domains…" value={search} onChange={e => setSearch(e.target.value)}
               style={{ maxWidth: 220 }} aria-label="Filter by domain" />
        <select className="src-filter" value={sort} onChange={e => setSort(e.target.value)} aria-label="Sort">
          {SORTS.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
        <label className="row" style={{ gap: 6, cursor: 'pointer', fontSize: 13.5 }}>
          <input type="checkbox" checked={problemsOnly} onChange={e => setProblemsOnly(e.target.checked)} />
          Problems only
        </label>
        <button type="button" className="secondary" onClick={exportCsv} disabled={!rows.length}>
          Export CSV
        </button>
      </div>
      {(search || problemsOnly) && (
        <p className="small muted" style={{ marginTop: -4, marginBottom: 12 }}>
          Showing {rows.length} of {allRows.length} domains.
        </p>
      )}

      {/* Headline totals across every domain. */}
      {t && (
        <div className="kpis" style={{ marginBottom: 18 }}>
          <div className="kpi"><div><div className="kpi-label">Messages</div><div className="kpi-value">{t.messages.toLocaleString()}</div><div className="kpi-sub">{t.domains} domain{t.domains === 1 ? '' : 's'} · {t.recipients.toLocaleString()} recipients</div></div></div>
          <div className="kpi"><div><div className="kpi-label">Delivered</div><div className="kpi-value">{t.delivered.toLocaleString()}</div><div className="kpi-sub">accepted by receivers</div></div></div>
          <div className="kpi"><div><div className="kpi-label">Bounced</div><div className="kpi-value">{t.bounced.toLocaleString()}</div><div className="kpi-sub">{t.deferred.toLocaleString()} deferred · {t.suppressed.toLocaleString()} suppressed</div></div></div>
          <div className="kpi"><div><div className="kpi-label">Engagement</div><div className="kpi-value">{t.opened.toLocaleString()}</div><div className="kpi-sub">opens · {t.clicked.toLocaleString()} clicks · {t.complained.toLocaleString()} complaints</div></div></div>
        </div>
      )}

      {isLoading && <div className="skeleton" style={{ height: 240 }} />}

      {!isLoading && rows.length === 0 && (
        <div className="empty">
          <div className="big">📊</div>
          <p><strong>No sending in this window</strong></p>
          <p>Send from a verified domain and its numbers will appear here.</p>
        </div>
      )}

      {rows.length > 0 && (
        <div className="card flush">
          <div style={{ overflowX: 'auto' }}>
            <table className="data">
              <thead>
                <tr>
                  <th>Domain</th>
                  <th className="num">Messages</th>
                  <th className="num">Recipients</th>
                  <th className="num">Delivered</th>
                  <th className="num">Bounced</th>
                  <th className="num">Deferred</th>
                  <th className="num">Complained</th>
                  <th className="num">Opened</th>
                  <th className="num">Clicked</th>
                  <th className="num">Delivery</th>
                  <th className="num">Bounce</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.domain}>
                    <td className="strong mono">{r.domain}</td>
                    <td className="num">{r.messages.toLocaleString()}</td>
                    <td className="num">{r.recipients.toLocaleString()}</td>
                    <td className="num">{r.delivered.toLocaleString()}</td>
                    <td className="num">{r.bounced.toLocaleString()}</td>
                    <td className="num">{r.deferred.toLocaleString()}</td>
                    <td className="num">{r.complained.toLocaleString()}</td>
                    <td className="num">{r.opened.toLocaleString()}</td>
                    <td className="num">{r.clicked.toLocaleString()}</td>
                    <td className="num">
                      <span className={r.delivery_rate != null && r.delivery_rate < 90 ? 'crit' : 'ok'}>{pct(r.delivery_rate)}</span>
                    </td>
                    <td className="num">
                      <span className={r.bounce_rate != null && r.bounce_rate > 5 ? 'crit' : ''}>{pct(r.bounce_rate)}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
